<template>
  <main class="catalog-admin">
    <section class="catalog-hero">
      <div class="catalog-hero__content">
        <span class="catalog-eyebrow">NOVO CANAL DE VENDAS</span>
        <h1>Sua vitrine digital, do seu jeito.</h1>
        <p>
          Publique produtos do ERP, apresente suas melhores ofertas e receba
          pedidos sem duplicar cadastros.
        </p>
      </div>

      <div class="catalog-hero__actions">
        <span
          class="catalog-status"
          :class="configuration.ativo ? 'is-online' : 'is-offline'"
        >
          <span class="catalog-status__dot"></span>
          {{ configuration.ativo ? "Catálogo ativo" : "Catálogo pausado" }}
        </span>
        <button
          type="button"
          class="catalog-button catalog-button--ghost"
          :disabled="!configuration.public_url"
          @click="openPublicCatalog"
        >
          <i class="la la-external-link-alt"></i>
          Abrir vitrine
        </button>
        <button
          v-if="activeTab === 'appearance'"
          type="button"
          class="catalog-button catalog-button--primary"
          :disabled="savingConfiguration || loadingConfiguration"
          @click="saveConfiguration"
        >
          <span v-if="savingConfiguration" class="catalog-spinner"></span>
          <i v-else class="la la-check"></i>
          {{ savingConfiguration ? "Salvando..." : "Salvar e publicar" }}
        </button>
      </div>
    </section>

    <nav class="catalog-tabs" aria-label="Configurações do catálogo">
      <button
        type="button"
        :class="{ active: activeTab === 'appearance' }"
        @click="activeTab = 'appearance'"
      >
        <span class="catalog-tab-icon"><i class="la la-paint-brush"></i></span>
        <span>
          <strong>Aparência e publicação</strong>
          <small>Marca, endereço e estilo da vitrine</small>
        </span>
      </button>
      <button
        type="button"
        :class="{ active: activeTab === 'promotions' }"
        @click="activeTab = 'promotions'"
      >
        <span class="catalog-tab-icon"><i class="la la-tags"></i></span>
        <span>
          <strong>Promoções</strong>
          <small>Ofertas programadas por produto</small>
        </span>
        <span class="catalog-tab-count">{{ promotions.length }}</span>
      </button>
      <button
        type="button"
        :class="{ active: activeTab === 'products' }"
        @click="activeTab = 'products'"
      >
        <span class="catalog-tab-icon"><i class="la la-box"></i></span>
        <span>
          <strong>Produtos</strong>
          <small>Fotos e itens publicados na vitrine</small>
        </span>
      </button>
    </nav>

    <section v-if="activeTab === 'appearance'" class="catalog-workspace">
      <div class="catalog-form-stack">
        <article class="catalog-panel catalog-publish-panel">
          <div class="catalog-panel__heading">
            <div>
              <span class="catalog-section-number">01</span>
              <div>
                <h2>Publicação</h2>
                <p>Defina a identidade e o endereço público do catálogo.</p>
              </div>
            </div>
            <label class="catalog-toggle">
              <input v-model="configuration.ativo" type="checkbox" />
              <span class="catalog-toggle__track">
                <span class="catalog-toggle__thumb"></span>
              </span>
              <span>{{ configuration.ativo ? "Ativo" : "Pausado" }}</span>
            </label>
          </div>

          <div v-if="loadingConfiguration" class="catalog-loading-block">
            <span class="catalog-spinner catalog-spinner--dark"></span>
            Carregando configurações...
          </div>

          <div v-else class="catalog-fields-grid">
            <label class="catalog-field catalog-field--wide">
              <span>Nome da loja <em>obrigatório</em></span>
              <input
                v-model.trim="configuration.nome"
                type="text"
                maxlength="100"
                placeholder="Ex.: Casa Aurora"
                @blur="fillSlugFromName"
              />
              <small v-if="fieldError('nome')" class="catalog-error">
                {{ fieldError("nome") }}
              </small>
            </label>

            <label class="catalog-field catalog-field--wide">
              <span>Endereço do catálogo <em>obrigatório</em></span>
              <div class="catalog-slug-input">
                <span>/</span>
                <input
                  v-model.trim="configuration.slug"
                  type="text"
                  maxlength="80"
                  placeholder="casa-aurora"
                  @input="normalizeSlug"
                />
              </div>
              <small v-if="fieldError('slug')" class="catalog-error">
                {{ fieldError("slug") }}
              </small>
              <small v-else>Use letras minúsculas, números e hífens.</small>
            </label>

            <label class="catalog-field catalog-field--wide">
              <span>Descrição da loja</span>
              <textarea
                v-model.trim="configuration.descricao"
                rows="3"
                maxlength="280"
                placeholder="Conte em poucas palavras o que torna sua loja especial."
              ></textarea>
              <small>{{ configuration.descricao.length }}/280 caracteres</small>
            </label>
          </div>
        </article>

        <article class="catalog-panel">
          <div class="catalog-panel__heading">
            <div>
              <span class="catalog-section-number">02</span>
              <div>
                <h2>Imagens da marca</h2>
                <p>Envie arquivos ou use imagens já hospedadas.</p>
              </div>
            </div>
          </div>

          <div class="catalog-assets-grid">
            <div class="catalog-asset-card">
              <div class="catalog-asset-preview catalog-asset-preview--logo">
                <img
                  v-if="previewLogo"
                  :src="previewLogo"
                  alt="Prévia do logo"
                />
                <span v-else>{{ storeInitials }}</span>
              </div>
              <div class="catalog-asset-copy">
                <strong>Logo da loja</strong>
                <small>PNG, JPG ou WebP · recomendado 600 × 600 px</small>
                <div class="catalog-asset-actions">
                  <input
                    ref="logoInput"
                    class="sr-only"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    @change="selectAsset('logo', $event)"
                  />
                  <button type="button" @click="$refs.logoInput.click()">
                    <i class="la la-upload"></i>
                    Escolher arquivo
                  </button>
                  <button
                    v-if="assets.logoFile"
                    type="button"
                    class="is-danger"
                    @click="clearAsset('logo')"
                  >
                    Remover
                  </button>
                </div>
                <input
                  v-model.trim="configuration.logo_url"
                  class="catalog-url-input"
                  type="url"
                  placeholder="ou cole a URL do logo"
                />
              </div>
            </div>

            <div class="catalog-asset-card">
              <div
                class="catalog-asset-preview catalog-asset-preview--cover"
                :style="coverPreviewStyle"
              >
                <span v-if="!previewCover"><i class="la la-image"></i></span>
              </div>
              <div class="catalog-asset-copy">
                <strong>Foto de capa</strong>
                <small>PNG, JPG ou WebP · recomendado 1600 × 700 px</small>
                <div class="catalog-asset-actions">
                  <input
                    ref="coverInput"
                    class="sr-only"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    @change="selectAsset('cover', $event)"
                  />
                  <button type="button" @click="$refs.coverInput.click()">
                    <i class="la la-upload"></i>
                    Escolher arquivo
                  </button>
                  <button
                    v-if="assets.coverFile"
                    type="button"
                    class="is-danger"
                    @click="clearAsset('cover')"
                  >
                    Remover
                  </button>
                </div>
                <input
                  v-model.trim="configuration.capa_url"
                  class="catalog-url-input"
                  type="url"
                  placeholder="ou cole a URL da foto de capa"
                />
              </div>
            </div>
          </div>
        </article>

        <article class="catalog-panel">
          <div class="catalog-panel__heading">
            <div>
              <span class="catalog-section-number">03</span>
              <div>
                <h2>Estilo da vitrine</h2>
                <p>Escolha uma base e personalize as cores da sua marca.</p>
              </div>
            </div>
          </div>

          <div class="catalog-presets">
            <button
              v-for="preset in themePresets"
              :key="preset.id"
              type="button"
              :class="{ selected: isPresetSelected(preset) }"
              @click="applyPreset(preset)"
            >
              <span class="catalog-preset-colors">
                <i :style="{ background: preset.tema.cor_primaria }"></i>
                <i :style="{ background: preset.tema.cor_secundaria }"></i>
                <i :style="{ background: preset.tema.cor_fundo }"></i>
              </span>
              <span>
                <strong>{{ preset.nome }}</strong>
                <small>{{ preset.descricao }}</small>
              </span>
              <i v-if="isPresetSelected(preset)" class="la la-check-circle"></i>
            </button>
          </div>

          <div class="catalog-colors-grid">
            <label
              v-for="color in themeColors"
              :key="color.key"
              class="catalog-color-field"
            >
              <span>{{ color.label }}</span>
              <div>
                <input v-model="configuration.tema[color.key]" type="color" />
                <input
                  v-model.trim="configuration.tema[color.key]"
                  type="text"
                  maxlength="7"
                  @blur="validateThemeColor(color.key)"
                />
              </div>
            </label>
          </div>

          <div class="catalog-style-grid">
            <label class="catalog-field">
              <span>Estilo dos produtos</span>
              <select v-model="configuration.tema.estilo_card">
                <option value="suave">Cards suaves</option>
                <option value="linha">Contorno editorial</option>
                <option value="elevado">Cards elevados</option>
              </select>
            </label>
            <label class="catalog-field">
              <span>Estilo dos botões</span>
              <select v-model="configuration.tema.estilo_botao">
                <option value="arredondado">Arredondados</option>
                <option value="pill">Formato cápsula</option>
                <option value="reto">Cantos discretos</option>
              </select>
            </label>
          </div>
        </article>
      </div>

      <aside class="catalog-preview-column">
        <div class="catalog-preview-heading">
          <div>
            <span>PRÉ-VISUALIZAÇÃO</span>
            <strong>Atualiza enquanto você edita</strong>
          </div>
          <span class="catalog-device-pill"
            ><i class="la la-mobile"></i> Mobile</span
          >
        </div>

        <div class="catalog-phone">
          <div class="catalog-phone__speaker"></div>
          <div
            class="catalog-phone__screen"
            :style="{ background: configuration.tema.cor_fundo }"
          >
            <div class="catalog-store-cover" :style="coverPreviewStyle">
              <div class="catalog-store-cover__shade"></div>
              <div
                class="catalog-store-logo"
                :style="{ background: configuration.tema.cor_primaria }"
              >
                <img v-if="previewLogo" :src="previewLogo" alt="" />
                <span v-else>{{ storeInitials }}</span>
              </div>
            </div>
            <div class="catalog-store-info">
              <span class="catalog-store-open">● catálogo online</span>
              <h3 :style="{ color: configuration.tema.cor_texto }">
                {{ configuration.nome || "Sua loja" }}
              </h3>
              <p :style="{ color: configuration.tema.cor_texto }">
                {{
                  configuration.descricao ||
                  "Produtos escolhidos com carinho para você."
                }}
              </p>
            </div>
            <div class="catalog-preview-search">
              <i class="la la-search"></i>
              Buscar no catálogo
            </div>
            <div class="catalog-preview-chips">
              <span
                :style="{
                  color: configuration.tema.cor_primaria,
                  borderColor: configuration.tema.cor_primaria,
                }"
                >Todos</span
              >
              <span>Novidades</span>
              <span>Mais vendidos</span>
            </div>
            <div class="catalog-preview-products">
              <div
                v-for="item in previewProducts"
                :key="item.name"
                class="catalog-preview-product"
                :class="`card-${configuration.tema.estilo_card}`"
              >
                <div :style="{ background: item.color }">
                  <span>{{ item.emoji }}</span>
                </div>
                <small>{{ item.tag }}</small>
                <strong :style="{ color: configuration.tema.cor_texto }">
                  {{ item.name }}
                </strong>
                <p>{{ item.price }}</p>
                <button
                  type="button"
                  :class="`button-${configuration.tema.estilo_botao}`"
                  :style="{
                    background: configuration.tema.cor_primaria,
                    color: buttonTextColor,
                  }"
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="catalog-preview-tip">
          <i class="la la-lightbulb"></i>
          <p>
            <strong>Dica de vitrine</strong>
            Use fotos claras e mantenha os nomes dos produtos curtos. Isso
            facilita a escolha no celular.
          </p>
        </div>
      </aside>
    </section>

    <section
      v-else-if="activeTab === 'promotions'"
      class="catalog-promotions-workspace"
    >
      <div class="catalog-promotions-summary">
        <div>
          <span class="catalog-summary-icon"><i class="la la-bolt"></i></span>
          <span>
            <strong>{{ activePromotionsCount }}</strong>
            promoções ativas agora
          </span>
        </div>
        <div>
          <span class="catalog-summary-icon is-soft"
            ><i class="la la-clock"></i
          ></span>
          <span>
            <strong>{{ scheduledPromotionsCount }}</strong>
            ofertas agendadas
          </span>
        </div>
        <p>
          As ofertas entram e saem da vitrine automaticamente conforme o período
          configurado.
        </p>
      </div>

      <div class="catalog-promotions-grid">
        <article class="catalog-panel catalog-promotion-form">
          <div class="catalog-panel__heading">
            <div>
              <span class="catalog-section-number">
                <i class="la la-plus"></i>
              </span>
              <div>
                <h2>
                  {{ promotionForm.id ? "Editar promoção" : "Nova promoção" }}
                </h2>
                <p>Programe uma oferta para um produto do catálogo.</p>
              </div>
            </div>
            <button
              v-if="promotionForm.id"
              type="button"
              class="catalog-text-button"
              @click="resetPromotionForm"
            >
              Cancelar edição
            </button>
          </div>

          <div v-if="loadingProducts" class="catalog-loading-block">
            <span class="catalog-spinner catalog-spinner--dark"></span>
            Carregando produtos do catálogo...
          </div>

          <div v-else class="catalog-fields-grid">
            <label class="catalog-field catalog-field--wide">
              <span>Produto <em>obrigatório</em></span>
              <select v-model="promotionForm.id_produto">
                <option :value="null" disabled>Selecione um produto</option>
                <option
                  v-for="product in products"
                  :key="product.id"
                  :value="product.id"
                >
                  {{ product.descricao }} ·
                  {{ formatMoney(product.valor_venda) }}
                </option>
              </select>
              <small v-if="promotionError('id_produto')" class="catalog-error">
                {{ promotionError("id_produto") }}
              </small>
              <small v-else-if="!products.length">
                Ative “Catálogo digital” nos produtos que deseja promover.
              </small>
            </label>

            <label class="catalog-field catalog-field--wide">
              <span>Nome da campanha <em>obrigatório</em></span>
              <input
                v-model.trim="promotionForm.nome"
                type="text"
                maxlength="100"
                placeholder="Ex.: Semana do cliente"
              />
              <small v-if="promotionError('nome')" class="catalog-error">
                {{ promotionError("nome") }}
              </small>
            </label>

            <label class="catalog-field">
              <span>Tipo de oferta</span>
              <select v-model="promotionForm.tipo">
                <option value="percentual">Desconto percentual</option>
                <option value="valor_fixo">Desconto em reais</option>
              </select>
            </label>

            <label class="catalog-field">
              <span>
                {{
                  promotionForm.tipo === "percentual" ? "Percentual" : "Valor"
                }}
                <em>obrigatório</em>
              </span>
              <div class="catalog-value-input">
                <span>{{
                  promotionForm.tipo === "percentual" ? "%" : "R$"
                }}</span>
                <input
                  v-model.trim="promotionForm.valor"
                  type="text"
                  inputmode="decimal"
                  placeholder="0,00"
                />
              </div>
              <small v-if="promotionError('valor')" class="catalog-error">
                {{ promotionError("valor") }}
              </small>
            </label>

            <label class="catalog-field">
              <span>Começa em</span>
              <input v-model="promotionForm.inicio_em" type="datetime-local" />
            </label>

            <label class="catalog-field">
              <span>Termina em</span>
              <input v-model="promotionForm.fim_em" type="datetime-local" />
              <small v-if="promotionError('fim_em')" class="catalog-error">
                {{ promotionError("fim_em") }}
              </small>
            </label>

            <label class="catalog-field catalog-field--wide">
              <span>Mensagem opcional</span>
              <textarea
                v-model.trim="promotionForm.descricao"
                rows="2"
                maxlength="180"
                placeholder="Uma chamada curta para destacar a oferta."
              ></textarea>
            </label>

            <div class="catalog-promotion-switches catalog-field--wide">
              <label class="catalog-check-row">
                <input v-model="promotionForm.ativo" type="checkbox" />
                <span>
                  <strong>Promoção ativa</strong>
                  <small>Pode aparecer na vitrine dentro do período.</small>
                </span>
              </label>
              <label class="catalog-check-row">
                <input v-model="promotionForm.destaque" type="checkbox" />
                <span>
                  <strong>Destacar na vitrine</strong>
                  <small>Use para sua oferta mais importante.</small>
                </span>
              </label>
            </div>

            <button
              type="button"
              class="catalog-button catalog-button--primary catalog-field--wide"
              :disabled="savingPromotion || !products.length"
              @click="savePromotion"
            >
              <span v-if="savingPromotion" class="catalog-spinner"></span>
              <i v-else class="la la-calendar-check"></i>
              {{
                savingPromotion
                  ? "Salvando..."
                  : promotionForm.id
                  ? "Atualizar promoção"
                  : "Agendar promoção"
              }}
            </button>
          </div>
        </article>

        <article class="catalog-panel catalog-promotion-list-panel">
          <div class="catalog-panel__heading">
            <div>
              <span class="catalog-section-number"
                ><i class="la la-list"></i
              ></span>
              <div>
                <h2>Campanhas configuradas</h2>
                <p>Acompanhe o calendário de ofertas da loja.</p>
              </div>
            </div>
            <button
              type="button"
              class="catalog-icon-button"
              :disabled="loadingPromotions"
              title="Atualizar lista"
              @click="loadPromotions"
            >
              <i class="la la-sync"></i>
            </button>
          </div>

          <div v-if="loadingPromotions" class="catalog-loading-block">
            <span class="catalog-spinner catalog-spinner--dark"></span>
            Carregando promoções...
          </div>

          <div v-else-if="!promotions.length" class="catalog-empty-state">
            <span><i class="la la-tags"></i></span>
            <h3>Sua primeira oferta começa aqui</h3>
            <p>Selecione um produto ao lado e programe a campanha.</p>
          </div>

          <div v-else class="catalog-promotion-list">
            <div
              v-for="promotion in promotions"
              :key="promotion.id"
              class="catalog-promotion-card"
            >
              <div class="catalog-promotion-card__visual">
                <img
                  v-if="promotionProduct(promotion).imagem"
                  :src="promotionProduct(promotion).imagem"
                  alt=""
                />
                <i v-else class="la la-shopping-bag"></i>
                <span v-if="promotion.destaque">Destaque</span>
              </div>
              <div class="catalog-promotion-card__body">
                <div class="catalog-promotion-card__topline">
                  <span
                    class="catalog-promotion-state"
                    :class="`is-${promotionStatus(promotion)}`"
                  >
                    {{ promotionStatusLabel(promotion) }}
                  </span>
                  <div>
                    <button
                      type="button"
                      title="Editar promoção"
                      @click="editPromotion(promotion)"
                    >
                      <i class="la la-pen"></i>
                    </button>
                    <button
                      type="button"
                      title="Excluir promoção"
                      :disabled="deletingPromotionId === promotion.id"
                      @click="removePromotion(promotion)"
                    >
                      <span
                        v-if="deletingPromotionId === promotion.id"
                        class="catalog-spinner catalog-spinner--dark"
                      ></span>
                      <i v-else class="la la-trash"></i>
                    </button>
                  </div>
                </div>
                <h3>{{ promotion.nome }}</h3>
                <p>{{ promotionProduct(promotion).descricao }}</p>
                <strong class="catalog-promotion-value">
                  {{ promotionValueLabel(promotion) }}
                </strong>
                <small>
                  <i class="la la-calendar"></i>
                  {{ promotionPeriodLabel(promotion) }}
                </small>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <CatalogoDigitalProductsTab
      ref="productsTab"
      v-show="activeTab === 'products'"
      @published="handleProductsPublished"
    />
  </main>
</template>

<script>
import CatalogoDigitalProductsTab from "../components/CatalogoDigitalProductsTab.vue";
import {
  CATALOG_THEME_PRESETS,
  DEFAULT_CATALOG_THEME,
  extractCatalogCollection,
  isCatalogConfigurationDraftDirty,
  isValidHexColor,
  normalizeCatalogConfiguration,
  normalizeCatalogProduct,
  normalizeCatalogPromotion,
  normalizeDecimal,
  promotionStatus,
  slugifyCatalogName,
} from "../catalogoDigitalConfig";
import {
  deleteCatalogPromotion,
  getCatalogConfiguration,
  getCatalogProducts,
  getCatalogPromotions,
  saveCatalogConfiguration,
  saveCatalogPromotion,
} from "../services/catalogoDigitalApi";

const createPromotionForm = () => ({
  id: null,
  id_produto: null,
  nome: "",
  descricao: "",
  tipo: "percentual",
  valor: "",
  inicio_em: "",
  fim_em: "",
  ativo: true,
  destaque: false,
});

const toLocalInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export default {
  name: "CatalogoDigitalAdmin",
  components: {
    CatalogoDigitalProductsTab,
  },
  data() {
    return {
      activeTab: "appearance",
      loadingConfiguration: false,
      loadingProducts: false,
      loadingPromotions: false,
      savingConfiguration: false,
      savingPromotion: false,
      deletingPromotionId: null,
      configuration: normalizeCatalogConfiguration(),
      configurationBaseline: normalizeCatalogConfiguration(),
      configurationErrors: {},
      products: [],
      promotions: [],
      promotionForm: createPromotionForm(),
      promotionErrors: {},
      assets: {
        logoFile: null,
        logoPreview: "",
        coverFile: null,
        coverPreview: "",
      },
      themePresets: CATALOG_THEME_PRESETS,
      themeColors: [
        { key: "cor_primaria", label: "Cor principal" },
        { key: "cor_secundaria", label: "Cor de destaque" },
        { key: "cor_texto", label: "Textos" },
        { key: "cor_fundo", label: "Fundo" },
      ],
      previewProducts: [
        {
          name: "Produto essencial",
          price: "R$ 49,90",
          tag: "Mais vendido",
          emoji: "✦",
          color: "#F1ECFF",
        },
        {
          name: "Seleção especial",
          price: "R$ 79,90",
          tag: "Novidade",
          emoji: "◌",
          color: "#FFF0E7",
        },
      ],
    };
  },
  computed: {
    configurationDirty() {
      return isCatalogConfigurationDraftDirty(
        this.configuration,
        this.configurationBaseline,
        Boolean(this.assets.logoFile || this.assets.coverFile)
      );
    },
    previewLogo() {
      return this.assets.logoPreview || this.configuration.logo_url;
    },
    previewCover() {
      return this.assets.coverPreview || this.configuration.capa_url;
    },
    coverPreviewStyle() {
      if (this.previewCover) {
        return {
          backgroundImage: `url(${this.previewCover})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
      }

      return {
        background: `linear-gradient(135deg, ${this.configuration.tema.cor_primaria}, ${this.configuration.tema.cor_secundaria})`,
      };
    },
    storeInitials() {
      const words = String(this.configuration.nome || "Sua Loja")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);
      return words.map((word) => word.charAt(0).toUpperCase()).join("") || "SL";
    },
    buttonTextColor() {
      const hex = this.configuration.tema.cor_primaria.replace("#", "");
      if (hex.length !== 6) return "#FFFFFF";
      const red = parseInt(hex.slice(0, 2), 16);
      const green = parseInt(hex.slice(2, 4), 16);
      const blue = parseInt(hex.slice(4, 6), 16);
      return red * 0.299 + green * 0.587 + blue * 0.114 > 170
        ? "#172033"
        : "#FFFFFF";
    },
    activePromotionsCount() {
      return this.promotions.filter((item) => promotionStatus(item) === "ativa")
        .length;
    },
    scheduledPromotionsCount() {
      return this.promotions.filter(
        (item) => promotionStatus(item) === "agendada"
      ).length;
    },
  },
  created() {
    this.loadConfiguration();
    this.loadProducts();
    this.loadPromotions();
    window.addEventListener(
      "beforeunload",
      this.handleConfigurationBeforeUnload
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "beforeunload",
      this.handleConfigurationBeforeUnload
    );
    this.revokePreview("logo");
    this.revokePreview("cover");
  },
  beforeRouteLeave(to, from, next) {
    const productsTab = this.$refs.productsTab;
    const productDraftDiscardApproved = Boolean(
      productsTab && productsTab.consumeRouteLeaveApproval()
    );
    if (productsTab && productsTab.saving) {
      this.$toast.warning("Aguarde a publicação dos produtos terminar.");
      next(false);
      return;
    }
    if (
      productsTab &&
      productsTab.hasDrafts &&
      !productDraftDiscardApproved &&
      !window.confirm(
        "Existem alterações não publicadas. Deseja descartá-las e sair desta tela?"
      )
    ) {
      next(false);
      return;
    }
    if (this.savingConfiguration) {
      this.$toast.warning("Aguarde a publicação da aparência terminar.");
      next(false);
      return;
    }
    if (
      this.configurationDirty &&
      !window.confirm(
        "Existem alterações de aparência não publicadas. Deseja descartá-las e sair desta tela?"
      )
    ) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    handleConfigurationBeforeUnload(event) {
      if (!this.configurationDirty || this.savingConfiguration) return;
      event.preventDefault();
      event.returnValue = "";
    },
    async handleProductsPublished(data) {
      const preserveAppearanceDraft = this.configurationDirty;
      const publishedConfiguration = data && data.configuracao;

      if (!preserveAppearanceDraft && publishedConfiguration) {
        const normalized = normalizeCatalogConfiguration(
          publishedConfiguration
        );
        this.configuration = normalized;
        this.configurationBaseline = normalizeCatalogConfiguration(normalized);
      }

      const refreshes = [this.loadProducts(), this.loadPromotions()];
      if (!preserveAppearanceDraft && !publishedConfiguration) {
        refreshes.push(this.loadConfiguration());
      }
      await Promise.all(refreshes);
    },
    async loadConfiguration() {
      this.loadingConfiguration = true;
      try {
        const data = await getCatalogConfiguration();
        const normalized = normalizeCatalogConfiguration(data || {});
        this.configuration = normalized;
        this.configurationBaseline = normalizeCatalogConfiguration(normalized);
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar o catálogo.");
      } finally {
        this.loadingConfiguration = false;
      }
    },
    async loadProducts() {
      this.loadingProducts = true;
      try {
        const data = await getCatalogProducts();
        this.products = extractCatalogCollection(data, "produtos").map(
          normalizeCatalogProduct
        );
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar os produtos.");
      } finally {
        this.loadingProducts = false;
      }
    },
    async loadPromotions() {
      this.loadingPromotions = true;
      try {
        const data = await getCatalogPromotions();
        this.promotions = extractCatalogCollection(data, "promocoes").map(
          normalizeCatalogPromotion
        );
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar as promoções.");
      } finally {
        this.loadingPromotions = false;
      }
    },
    fillSlugFromName() {
      if (!this.configuration.slug) {
        this.configuration.slug = slugifyCatalogName(this.configuration.nome);
      }
    },
    normalizeSlug() {
      this.configuration.slug = slugifyCatalogName(this.configuration.slug);
    },
    fieldError(field) {
      const value = this.configurationErrors[field];
      return Array.isArray(value) ? value[0] : value || "";
    },
    promotionError(field) {
      const value = this.promotionErrors[field];
      return Array.isArray(value) ? value[0] : value || "";
    },
    validateConfiguration() {
      const errors = {};
      if (!this.configuration.nome.trim())
        errors.nome = "Informe o nome da loja.";
      if (!this.configuration.slug.trim())
        errors.slug = "Informe o endereço do catálogo.";
      this.themeColors.forEach((color) => {
        if (!isValidHexColor(this.configuration.tema[color.key])) {
          errors.tema = "Revise as cores do tema.";
        }
      });
      this.configurationErrors = errors;
      return Object.keys(errors).length === 0;
    },
    validateThemeColor(key) {
      if (!isValidHexColor(this.configuration.tema[key])) {
        this.configuration.tema[key] = DEFAULT_CATALOG_THEME[key];
        this.$toast.warning("Use uma cor hexadecimal com seis dígitos.");
      }
    },
    async saveConfiguration() {
      this.normalizeSlug();
      if (!this.validateConfiguration()) {
        this.$toast.error("Revise os campos destacados antes de publicar.");
        return;
      }

      this.savingConfiguration = true;
      const payload = new FormData();
      ["nome", "slug", "descricao", "logo_url", "capa_url"].forEach((key) => {
        payload.append(key, this.configuration[key] || "");
      });
      payload.append("ativo", this.configuration.ativo ? "1" : "0");
      payload.append("tema", JSON.stringify(this.configuration.tema));
      if (this.assets.logoFile) payload.append("logo", this.assets.logoFile);
      if (this.assets.coverFile) payload.append("capa", this.assets.coverFile);

      try {
        const data = await saveCatalogConfiguration(payload);
        this.configuration = normalizeCatalogConfiguration(
          data && data.configuracao
            ? data.configuracao
            : data || this.configuration
        );
        this.configurationBaseline = normalizeCatalogConfiguration(
          this.configuration
        );
        this.clearAsset("logo");
        this.clearAsset("cover");
        this.configurationErrors = {};
        this.$toast.success("Catálogo atualizado com sucesso.");
      } catch (error) {
        this.configurationErrors =
          error.errors || (error.response && error.response.data.errors) || {};
        this.notifyError(error, "Não foi possível salvar o catálogo.");
      } finally {
        this.savingConfiguration = false;
      }
    },
    selectAsset(type, event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
        this.$toast.error("Escolha uma imagem PNG, JPG ou WebP.");
        event.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error("A imagem deve ter no máximo 5 MB.");
        event.target.value = "";
        return;
      }

      const isLogo = type === "logo";
      this.revokePreview(type);
      this.assets[isLogo ? "logoFile" : "coverFile"] = file;
      this.assets[isLogo ? "logoPreview" : "coverPreview"] =
        URL.createObjectURL(file);
    },
    revokePreview(type) {
      const key = type === "logo" ? "logoPreview" : "coverPreview";
      if (this.assets[key] && this.assets[key].startsWith("blob:")) {
        URL.revokeObjectURL(this.assets[key]);
      }
    },
    clearAsset(type) {
      const isLogo = type === "logo";
      this.revokePreview(type);
      this.assets[isLogo ? "logoFile" : "coverFile"] = null;
      this.assets[isLogo ? "logoPreview" : "coverPreview"] = "";
      const input = this.$refs[isLogo ? "logoInput" : "coverInput"];
      if (input) input.value = "";
    },
    applyPreset(preset) {
      this.configuration.tema = { ...preset.tema };
    },
    isPresetSelected(preset) {
      return Object.keys(preset.tema).every(
        (key) => preset.tema[key] === this.configuration.tema[key]
      );
    },
    openPublicCatalog() {
      if (this.configuration.public_url) {
        window.open(this.configuration.public_url, "_blank", "noopener");
      }
    },
    validatePromotion() {
      const errors = {};
      const value = normalizeDecimal(this.promotionForm.valor);
      if (!this.promotionForm.id_produto)
        errors.id_produto = "Selecione um produto.";
      if (!this.promotionForm.nome.trim())
        errors.nome = "Informe o nome da campanha.";
      if (!value || Number(value) <= 0)
        errors.valor = "Informe um valor maior que zero.";
      if (this.promotionForm.tipo === "percentual" && Number(value) > 100) {
        errors.valor = "O percentual deve ser de no máximo 100%.";
      }
      const start = toIsoValue(this.promotionForm.inicio_em);
      const end = toIsoValue(this.promotionForm.fim_em);
      if (start && end && new Date(start) >= new Date(end)) {
        errors.fim_em = "O término deve ser posterior ao início.";
      }
      this.promotionErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async savePromotion() {
      if (!this.validatePromotion()) {
        this.$toast.error("Revise os dados da promoção.");
        return;
      }

      this.savingPromotion = true;
      const payload = {
        id: this.promotionForm.id || undefined,
        nome: this.promotionForm.nome.trim(),
        descricao: this.promotionForm.descricao.trim(),
        tipo: this.promotionForm.tipo,
        valor: normalizeDecimal(this.promotionForm.valor),
        escopo: "produto",
        id_produto: this.promotionForm.id_produto,
        inicio_em: toIsoValue(this.promotionForm.inicio_em),
        fim_em: toIsoValue(this.promotionForm.fim_em),
        ativo: this.promotionForm.ativo,
        destaque: this.promotionForm.destaque,
        ordem: 0,
      };

      try {
        await saveCatalogPromotion(payload);
        this.$toast.success(
          this.promotionForm.id
            ? "Promoção atualizada com sucesso."
            : "Promoção agendada com sucesso."
        );
        this.resetPromotionForm();
        await this.loadPromotions();
      } catch (error) {
        this.promotionErrors =
          error.errors || (error.response && error.response.data.errors) || {};
        this.notifyError(error, "Não foi possível salvar a promoção.");
      } finally {
        this.savingPromotion = false;
      }
    },
    editPromotion(promotion) {
      this.promotionForm = {
        id: promotion.id,
        id_produto: promotion.id_produto,
        nome: promotion.nome,
        descricao: promotion.descricao,
        tipo: promotion.tipo,
        valor: promotion.valor,
        inicio_em: toLocalInputValue(promotion.inicio_em),
        fim_em: toLocalInputValue(promotion.fim_em),
        ativo: promotion.ativo,
        destaque: promotion.destaque,
      };
      this.promotionErrors = {};
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    resetPromotionForm() {
      this.promotionForm = createPromotionForm();
      this.promotionErrors = {};
    },
    async removePromotion(promotion) {
      const confirmed = window.confirm(
        `Excluir a promoção “${promotion.nome}”? Esta ação não pode ser desfeita.`
      );
      if (!confirmed) return;

      this.deletingPromotionId = promotion.id;
      try {
        await deleteCatalogPromotion(promotion.id);
        this.promotions = this.promotions.filter(
          (item) => item.id !== promotion.id
        );
        if (this.promotionForm.id === promotion.id) this.resetPromotionForm();
        this.$toast.success("Promoção excluída com sucesso.");
      } catch (error) {
        this.notifyError(error, "Não foi possível excluir a promoção.");
      } finally {
        this.deletingPromotionId = null;
      }
    },
    promotionProduct(promotion) {
      return (
        this.products.find(
          (product) => String(product.id) === String(promotion.id_produto)
        ) || {
          descricao: promotion.produto_nome || "Produto do catálogo",
          imagem: promotion.produto_imagem || "",
        }
      );
    },
    promotionStatus,
    promotionStatusLabel(promotion) {
      return {
        ativa: "Ativa agora",
        agendada: "Agendada",
        encerrada: "Encerrada",
        inativa: "Inativa",
      }[promotionStatus(promotion)];
    },
    promotionValueLabel(promotion) {
      return promotion.tipo === "percentual"
        ? `${Number(promotion.valor).toLocaleString("pt-BR")}% OFF`
        : `${this.formatMoney(promotion.valor)} de desconto`;
    },
    promotionPeriodLabel(promotion) {
      if (!promotion.inicio_em && !promotion.fim_em)
        return "Sem período definido";
      const start = promotion.inicio_em
        ? this.formatDate(promotion.inicio_em)
        : "agora";
      const end = promotion.fim_em
        ? this.formatDate(promotion.fim_em)
        : "sem fim";
      return `${start} → ${end}`;
    },
    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Data inválida";
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    notifyError(error, fallback) {
      const message =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.message) ||
        (error && error.message) ||
        fallback;
      this.$toast.error(message || fallback);
    },
  },
};
</script>

<style scoped>
.catalog-admin {
  --ink: #172033;
  --muted: #6b7280;
  --line: #e7e9f0;
  --brand: var(--primary-color, #ee7361);
  --brand-dark: var(--primary-hover-color, var(--brand));
  --surface: #ffffff;
  min-height: 100%;
  padding: 22px 26px 30px;
  color: var(--ink);
  background: radial-gradient(
      circle at 93% -10%,
      color-mix(in srgb, var(--brand) 12%, transparent 88%),
      transparent 28%
    ),
    #f5f6fa;
  font-family: Inter, "Segoe UI", sans-serif;
}

.catalog-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  overflow: hidden;
  min-height: 124px;
  padding: 20px 24px;
  border-radius: 18px;
  color: #fff;
  background: radial-gradient(
      circle at 79% 20%,
      rgba(255, 255, 255, 0.2),
      transparent 23%
    ),
    linear-gradient(120deg, var(--brand-dark) 0%, var(--brand) 100%);
  box-shadow: 0 12px 30px color-mix(in srgb, var(--brand) 24%, transparent 76%);
}

.catalog-hero::after {
  content: "";
  position: absolute;
  right: -42px;
  top: -108px;
  width: 210px;
  height: 210px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  box-shadow: 0 0 0 34px rgba(255, 255, 255, 0.035),
    0 0 0 72px rgba(255, 255, 255, 0.025);
}

.catalog-hero__content,
.catalog-hero__actions {
  position: relative;
  z-index: 1;
}

.catalog-eyebrow {
  display: inline-flex;
  margin-bottom: 7px;
  padding: 4px 8px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 99px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.08);
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.14em;
}

.catalog-hero h1 {
  margin: 0;
  font-size: clamp(23px, 2.2vw, 31px);
  font-weight: 800;
  letter-spacing: -0.04em;
}

.catalog-hero p {
  max-width: 610px;
  margin: 5px 0 0;
  color: rgba(255, 255, 255, 0.74);
  font-size: 12px;
  line-height: 1.45;
}

.catalog-hero__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  min-width: 390px;
}

.catalog-status {
  display: flex;
  align-items: center;
  flex-basis: 100%;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 2px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 700;
}

.catalog-status__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #aab0bf;
  box-shadow: 0 0 0 4px rgba(170, 176, 191, 0.15);
}

.catalog-status.is-online .catalog-status__dot {
  background: #58e4a2;
  box-shadow: 0 0 0 4px rgba(88, 228, 162, 0.16);
}

.catalog-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 15px;
  border: 0;
  border-radius: 11px;
  font-size: 13px;
  font-weight: 750;
  transition: 0.2s ease;
}

.catalog-button:not(:disabled):hover {
  transform: translateY(-1px);
}

.catalog-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.catalog-button--ghost {
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.09);
}

.catalog-button--primary {
  color: #fff;
  background: var(--brand);
  box-shadow: 0 8px 20px color-mix(in srgb, var(--brand) 25%, transparent 75%);
}

.catalog-hero .catalog-button--primary {
  color: var(--brand-dark);
  background: #fff;
  box-shadow: none;
}

.catalog-tabs {
  display: flex;
  gap: 10px;
  margin: 14px 0 18px;
  padding: 6px;
  border: 1px solid var(--line);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 7px 24px rgba(31, 38, 61, 0.045);
}

.catalog-tabs > button {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  flex: 1;
  padding: 11px 15px;
  border: 0;
  border-radius: 12px;
  color: var(--muted);
  text-align: left;
  background: transparent;
}

.catalog-tabs > button.active {
  color: var(--ink);
  background: color-mix(in srgb, var(--brand) 10%, #ffffff 90%);
}

.catalog-tabs strong,
.catalog-tabs small {
  display: block;
}

.catalog-tabs strong {
  font-size: 13px;
}
.catalog-tabs small {
  margin-top: 2px;
  color: #8a90a0;
  font-size: 11px;
}

.catalog-tab-icon {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  color: var(--brand);
  background: #fff;
  font-size: 18px;
}

.catalog-tab-count {
  margin-left: auto;
  padding: 3px 8px;
  border-radius: 99px;
  color: var(--brand);
  background: #fff;
  font-size: 11px;
  font-weight: 800;
}

.catalog-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(330px, 0.75fr);
  align-items: start;
  gap: 22px;
}

.catalog-form-stack {
  display: grid;
  gap: 18px;
}

.catalog-panel {
  padding: 25px;
  border: 1px solid var(--line);
  border-radius: 20px;
  background: var(--surface);
  box-shadow: 0 8px 28px rgba(31, 38, 61, 0.045);
}

.catalog-panel__heading,
.catalog-panel__heading > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 13px;
}

.catalog-panel__heading {
  margin-bottom: 23px;
}

.catalog-panel__heading h2 {
  margin: 0;
  color: var(--ink);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.catalog-panel__heading p {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.catalog-section-number {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  color: var(--brand);
  background: color-mix(in srgb, var(--brand) 10%, #ffffff 90%);
  font-size: 11px;
  font-weight: 900;
}

.catalog-toggle {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  color: #4b5262;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.catalog-toggle input {
  position: absolute;
  opacity: 0;
}
.catalog-toggle__track {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 99px;
  background: #cbd0da;
  transition: 0.2s;
}
.catalog-toggle__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: 0.2s;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}
.catalog-toggle input:checked + .catalog-toggle__track {
  background: var(--brand);
}
.catalog-toggle input:checked + .catalog-toggle__track .catalog-toggle__thumb {
  transform: translateX(18px);
}

.catalog-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 17px;
}

.catalog-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 0;
}

.catalog-field--wide {
  grid-column: 1 / -1;
}
.catalog-field > span {
  color: #3e4555;
  font-size: 12px;
  font-weight: 750;
}
.catalog-field em {
  margin-left: 4px;
  color: var(--brand);
  font-size: 9px;
  font-style: normal;
  font-weight: 800;
  text-transform: uppercase;
}
.catalog-field > small {
  color: #8b91a1;
  font-size: 10px;
}

.catalog-field input,
.catalog-field textarea,
.catalog-field select,
.catalog-url-input {
  width: 100%;
  min-height: 43px;
  padding: 10px 12px;
  border: 1px solid #dfe2ea;
  border-radius: 10px;
  outline: 0;
  color: var(--ink);
  background: #fff;
  font-size: 13px;
  transition: 0.18s;
}

.catalog-field textarea {
  min-height: auto;
  resize: vertical;
}
.catalog-field input:focus,
.catalog-field textarea:focus,
.catalog-field select:focus,
.catalog-url-input:focus {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 12%, transparent 88%);
}
.catalog-error {
  color: #d64056 !important;
  font-weight: 650;
}

.catalog-slug-input,
.catalog-value-input {
  display: flex;
  overflow: hidden;
  border: 1px solid #dfe2ea;
  border-radius: 10px;
  background: #f6f7fa;
}

.catalog-slug-input:focus-within,
.catalog-value-input:focus-within {
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 12%, transparent 88%);
}
.catalog-slug-input > span,
.catalog-value-input > span {
  display: flex;
  align-items: center;
  padding: 0 12px;
  color: #838998;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.catalog-slug-input input,
.catalog-value-input input {
  border: 0;
  border-left: 1px solid #e4e6ec;
  border-radius: 0;
  box-shadow: none !important;
}

.catalog-loading-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 110px;
  color: var(--muted);
  font-size: 12px;
}

.catalog-spinner {
  display: inline-block;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: catalog-spin 0.75s linear infinite;
}

.catalog-spinner--dark {
  color: var(--brand);
  border-color: color-mix(in srgb, var(--brand) 24%, #ffffff 76%);
  border-top-color: currentColor;
}
@keyframes catalog-spin {
  to {
    transform: rotate(360deg);
  }
}

.catalog-assets-grid {
  display: grid;
  gap: 14px;
}
.catalog-asset-card {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 17px;
  align-items: center;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #fbfbfd;
}
.catalog-asset-preview {
  display: grid;
  place-items: center;
  overflow: hidden;
  height: 88px;
  border-radius: 12px;
  color: #fff;
  background: linear-gradient(135deg, var(--brand-dark), var(--brand));
}
.catalog-asset-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.catalog-asset-preview--logo {
  width: 88px;
  border-radius: 50%;
  font-size: 25px;
  font-weight: 850;
}
.catalog-asset-preview--cover {
  width: 112px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 26px;
}
.catalog-asset-copy {
  display: grid;
  gap: 5px;
}
.catalog-asset-copy strong {
  font-size: 13px;
}
.catalog-asset-copy small {
  color: var(--muted);
  font-size: 10px;
}
.catalog-asset-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}
.catalog-asset-actions button,
.catalog-text-button {
  padding: 0;
  border: 0;
  color: var(--brand);
  background: none;
  font-size: 11px;
  font-weight: 750;
}
.catalog-asset-actions button.is-danger {
  color: #d64056;
}
.catalog-url-input {
  min-height: 36px;
  margin-top: 5px;
  padding: 7px 10px;
  font-size: 11px;
}

.catalog-presets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 22px;
}
.catalog-presets > button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  color: var(--ink);
  text-align: left;
  background: #fff;
}
.catalog-presets > button.selected {
  border-color: var(--brand);
  background: color-mix(in srgb, var(--brand) 6%, #ffffff 94%);
  box-shadow: inset 0 0 0 1px var(--brand);
}
.catalog-presets > button > span:nth-child(2) {
  min-width: 0;
}
.catalog-presets strong,
.catalog-presets small {
  display: block;
}
.catalog-presets strong {
  font-size: 12px;
}
.catalog-presets small {
  margin-top: 2px;
  overflow: hidden;
  color: var(--muted);
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.catalog-presets > button > .la {
  margin-left: auto;
  color: var(--brand);
}
.catalog-preset-colors {
  display: flex;
}
.catalog-preset-colors i {
  width: 18px;
  height: 32px;
  border: 2px solid #fff;
}
.catalog-preset-colors i:first-child {
  border-radius: 8px 0 0 8px;
}
.catalog-preset-colors i:last-child {
  border-radius: 0 8px 8px 0;
}

.catalog-colors-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}
.catalog-color-field {
  display: grid;
  gap: 7px;
  margin: 0;
}
.catalog-color-field > span {
  color: #4b5262;
  font-size: 10px;
  font-weight: 700;
}
.catalog-color-field > div {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px;
  border: 1px solid var(--line);
  border-radius: 10px;
}
.catalog-color-field input[type="color"] {
  width: 29px;
  height: 29px;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  cursor: pointer;
}
.catalog-color-field input[type="text"] {
  min-width: 0;
  width: 100%;
  border: 0;
  outline: 0;
  color: #515867;
  font-family: monospace;
  font-size: 10px;
  text-transform: uppercase;
}
.catalog-style-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.catalog-preview-column {
  position: sticky;
  top: 20px;
  padding: 24px;
  border: 1px solid var(--line);
  border-radius: 20px;
  background: #eceef4;
}
.catalog-preview-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 17px;
}
.catalog-preview-heading span,
.catalog-preview-heading strong {
  display: block;
}
.catalog-preview-heading > div > span {
  color: #8a90a0;
  font-size: 8px;
  font-weight: 850;
  letter-spacing: 0.13em;
}
.catalog-preview-heading strong {
  margin-top: 3px;
  font-size: 11px;
}
.catalog-device-pill {
  padding: 5px 9px;
  border-radius: 99px;
  color: #666d7d;
  background: #fff;
  font-size: 9px !important;
  font-weight: 700 !important;
}

.catalog-phone {
  width: min(100%, 330px);
  margin: auto;
  padding: 9px;
  border-radius: 33px;
  background: #202331;
  box-shadow: 0 22px 42px rgba(29, 34, 50, 0.24);
}
.catalog-phone__speaker {
  width: 61px;
  height: 5px;
  margin: 2px auto 8px;
  border-radius: 99px;
  background: #3c4050;
}
.catalog-phone__screen {
  overflow: hidden;
  min-height: 560px;
  border-radius: 24px;
  background: #fff;
}
.catalog-store-cover {
  position: relative;
  height: 130px;
  background: linear-gradient(135deg, var(--brand-dark), var(--brand));
}
.catalog-store-cover__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.25));
}
.catalog-store-logo {
  position: absolute;
  left: 18px;
  bottom: -23px;
  display: grid;
  place-items: center;
  overflow: hidden;
  width: 54px;
  height: 54px;
  border: 4px solid #fff;
  border-radius: 17px;
  color: #fff;
  background: var(--brand);
  font-size: 15px;
  font-weight: 850;
  box-shadow: 0 5px 16px rgba(0, 0, 0, 0.16);
}
.catalog-store-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.catalog-store-info {
  padding: 31px 18px 10px;
}
.catalog-store-open {
  color: #1c9a68;
  font-size: 7px;
  font-weight: 850;
  text-transform: uppercase;
}
.catalog-store-info h3 {
  margin: 5px 0 3px;
  font-size: 18px;
  font-weight: 850;
  letter-spacing: -0.03em;
}
.catalog-store-info p {
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  opacity: 0.65;
  font-size: 9px;
  line-height: 1.45;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.catalog-preview-search {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 5px 18px 10px;
  padding: 9px 10px;
  border: 1px solid rgba(114, 121, 140, 0.16);
  border-radius: 9px;
  color: #9ca1ad;
  background: rgba(255, 255, 255, 0.65);
  font-size: 9px;
}
.catalog-preview-chips {
  display: flex;
  gap: 6px;
  overflow: hidden;
  padding: 0 18px 12px;
}
.catalog-preview-chips span {
  flex: 0 0 auto;
  padding: 5px 8px;
  border: 1px solid rgba(114, 121, 140, 0.18);
  border-radius: 99px;
  color: #7d8391;
  background: rgba(255, 255, 255, 0.55);
  font-size: 7px;
  font-weight: 750;
}
.catalog-preview-products {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 9px;
  padding: 0 18px 18px;
}
.catalog-preview-product {
  padding: 7px;
  border: 1px solid rgba(114, 121, 140, 0.13);
  border-radius: 11px;
  background: #fff;
}
.catalog-preview-product.card-elevado {
  border-color: transparent;
  box-shadow: 0 7px 16px rgba(35, 40, 57, 0.09);
}
.catalog-preview-product.card-linha {
  border-color: rgba(40, 43, 52, 0.35);
  border-radius: 3px;
  box-shadow: none;
}
.catalog-preview-product > div {
  display: grid;
  place-items: center;
  height: 84px;
  margin-bottom: 7px;
  border-radius: 8px;
  font-size: 27px;
}
.catalog-preview-product small {
  color: #a06c3c;
  font-size: 6px;
  font-weight: 850;
  text-transform: uppercase;
}
.catalog-preview-product strong {
  display: block;
  margin: 3px 0;
  overflow: hidden;
  font-size: 9px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.catalog-preview-product p {
  margin: 0 0 7px;
  color: #333846;
  font-size: 10px;
  font-weight: 850;
}
.catalog-preview-product button {
  width: 100%;
  padding: 6px;
  border: 0;
  border-radius: 6px;
  font-size: 7px;
  font-weight: 800;
}
.catalog-preview-product button.button-pill {
  border-radius: 99px;
}
.catalog-preview-product button.button-reto {
  border-radius: 2px;
}
.catalog-preview-tip {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  padding: 12px;
  border-radius: 12px;
  color: #5c6372;
  background: rgba(255, 255, 255, 0.7);
}
.catalog-preview-tip > i {
  color: #d49536;
  font-size: 18px;
}
.catalog-preview-tip p {
  margin: 0;
  font-size: 9px;
  line-height: 1.5;
}
.catalog-preview-tip strong {
  display: block;
  color: #373d4a;
  font-size: 10px;
}

.catalog-promotions-workspace {
  display: grid;
  gap: 18px;
}
.catalog-promotions-summary {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 18px 23px;
  border: 1px solid color-mix(in srgb, var(--brand) 24%, #ffffff 76%);
  border-radius: 17px;
  background: linear-gradient(
    110deg,
    color-mix(in srgb, var(--brand) 8%, #ffffff 92%),
    #fff
  );
}
.catalog-promotions-summary > div {
  display: flex;
  align-items: center;
  gap: 11px;
  color: #6c7280;
  font-size: 11px;
}
.catalog-promotions-summary strong {
  display: block;
  color: var(--ink);
  font-size: 21px;
  line-height: 1;
}
.catalog-summary-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  color: #fff;
  background: var(--brand);
  font-size: 18px;
}
.catalog-summary-icon.is-soft {
  color: var(--brand);
  background: color-mix(in srgb, var(--brand) 12%, #ffffff 88%);
}
.catalog-promotions-summary p {
  max-width: 380px;
  margin: 0 0 0 auto;
  color: #777d8b;
  font-size: 11px;
  line-height: 1.5;
}
.catalog-promotions-grid {
  display: grid;
  grid-template-columns: minmax(350px, 0.72fr) minmax(0, 1.28fr);
  align-items: start;
  gap: 18px;
}
.catalog-promotion-form {
  position: sticky;
  top: 20px;
}
.catalog-promotion-switches {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 9px;
}
.catalog-check-row {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 0;
  padding: 11px;
  border: 1px solid var(--line);
  border-radius: 10px;
  cursor: pointer;
}
.catalog-check-row input {
  margin-top: 2px;
  accent-color: var(--brand);
}
.catalog-check-row strong,
.catalog-check-row small {
  display: block;
}
.catalog-check-row strong {
  color: #454b59;
  font-size: 11px;
}
.catalog-check-row small {
  margin-top: 2px;
  color: #8a90a0;
  font-size: 9px;
  line-height: 1.4;
}
.catalog-text-button {
  white-space: nowrap;
}
.catalog-icon-button {
  display: grid;
  place-items: center;
  width: 35px;
  height: 35px;
  border: 1px solid var(--line);
  border-radius: 10px;
  color: #707787;
  background: #fff;
}
.catalog-empty-state {
  display: grid;
  justify-items: center;
  min-height: 330px;
  align-content: center;
  color: var(--muted);
  text-align: center;
}
.catalog-empty-state > span {
  display: grid;
  place-items: center;
  width: 65px;
  height: 65px;
  margin-bottom: 15px;
  border-radius: 20px;
  color: var(--brand);
  background: color-mix(in srgb, var(--brand) 10%, #ffffff 90%);
  font-size: 29px;
}
.catalog-empty-state h3 {
  margin: 0 0 7px;
  color: var(--ink);
  font-size: 16px;
}
.catalog-empty-state p {
  margin: 0;
  font-size: 11px;
}
.catalog-promotion-list {
  display: grid;
  gap: 11px;
}
.catalog-promotion-card {
  display: grid;
  grid-template-columns: 105px minmax(0, 1fr);
  overflow: hidden;
  min-height: 140px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #fff;
  transition: 0.18s;
}
.catalog-promotion-card:hover {
  border-color: color-mix(in srgb, var(--brand) 38%, #ffffff 62%);
  box-shadow: 0 8px 22px rgba(38, 43, 61, 0.07);
  transform: translateY(-1px);
}
.catalog-promotion-card__visual {
  position: relative;
  display: grid;
  place-items: center;
  overflow: hidden;
  color: var(--brand);
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--brand) 13%, #ffffff 87%),
    color-mix(in srgb, var(--brand) 4%, #ffffff 96%)
  );
  font-size: 31px;
}
.catalog-promotion-card__visual img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.catalog-promotion-card__visual span {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 4px 6px;
  border-radius: 5px;
  color: #fff;
  background: #27243b;
  font-size: 7px;
  font-weight: 800;
  text-transform: uppercase;
}
.catalog-promotion-card__body {
  min-width: 0;
  padding: 13px 15px;
}
.catalog-promotion-card__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.catalog-promotion-card__topline > div {
  display: flex;
  gap: 3px;
}
.catalog-promotion-card__topline button {
  display: grid;
  place-items: center;
  width: 27px;
  height: 27px;
  border: 0;
  border-radius: 7px;
  color: #7e8492;
  background: #f5f6f8;
}
.catalog-promotion-state {
  padding: 4px 7px;
  border-radius: 99px;
  font-size: 7px;
  font-weight: 850;
  text-transform: uppercase;
}
.catalog-promotion-state.is-ativa {
  color: #14835a;
  background: #e4f8ef;
}
.catalog-promotion-state.is-agendada {
  color: var(--brand);
  background: color-mix(in srgb, var(--brand) 12%, #ffffff 88%);
}
.catalog-promotion-state.is-encerrada,
.catalog-promotion-state.is-inativa {
  color: #747b89;
  background: #eceef1;
}
.catalog-promotion-card h3 {
  margin: 7px 0 2px;
  overflow: hidden;
  color: var(--ink);
  font-size: 13px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.catalog-promotion-card p {
  margin: 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.catalog-promotion-value {
  display: block;
  margin: 7px 0 4px;
  color: var(--brand);
  font-size: 14px;
}
.catalog-promotion-card__body > small {
  color: #878d9a;
  font-size: 8px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .catalog-workspace {
    grid-template-columns: minmax(0, 1fr) 330px;
  }
  .catalog-colors-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .catalog-presets {
    grid-template-columns: 1fr;
  }
  .catalog-promotions-grid {
    grid-template-columns: 390px minmax(0, 1fr);
  }
}

@media (max-width: 991px) {
  .catalog-admin {
    padding: 20px;
  }
  .catalog-hero {
    align-items: flex-start;
    flex-direction: column;
    padding: 20px 22px;
  }
  .catalog-hero__actions {
    justify-content: flex-start;
    min-width: 0;
  }
  .catalog-status {
    justify-content: flex-start;
  }
  .catalog-workspace,
  .catalog-promotions-grid {
    grid-template-columns: 1fr;
  }
  .catalog-preview-column,
  .catalog-promotion-form {
    position: static;
  }
  .catalog-phone {
    max-width: 330px;
  }
}

@media (max-width: 640px) {
  .catalog-admin {
    padding: 12px;
  }
  .catalog-hero {
    padding: 18px;
    border-radius: 19px;
  }
  .catalog-hero__actions {
    width: 100%;
  }
  .catalog-hero__actions .catalog-button {
    flex: 1;
  }
  .catalog-tabs {
    align-items: stretch;
    flex-direction: column;
  }
  .catalog-tabs small {
    display: none;
  }
  .catalog-panel {
    padding: 19px 16px;
    border-radius: 16px;
  }
  .catalog-panel__heading {
    align-items: flex-start;
  }
  .catalog-fields-grid,
  .catalog-style-grid,
  .catalog-promotion-switches {
    grid-template-columns: 1fr;
  }
  .catalog-assets-grid {
    gap: 10px;
  }
  .catalog-asset-card {
    grid-template-columns: 1fr;
  }
  .catalog-asset-preview--cover {
    width: 100%;
  }
  .catalog-colors-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .catalog-promotions-summary {
    align-items: stretch;
    flex-direction: column;
    gap: 13px;
  }
  .catalog-promotions-summary p {
    margin-left: 0;
  }
  .catalog-promotion-card {
    grid-template-columns: 82px minmax(0, 1fr);
  }
}
</style>
